/**
 * Hindi render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'hi')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const hindiLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'धुंधला' },
    change: { primary: 'परिवर्तन' },
    click: { primary: 'क्लिक' },
    dblclick: { primary: 'डबल_क्लिक' },
    focus: { primary: 'फोकस' },
    input: { primary: 'इनपुट' },
    keydown: { primary: 'की_नीचे' },
    keypress: { primary: 'की_प्रेस' },
    keyup: { primary: 'की_ऊपर' },
    load: { primary: 'लोड' },
    mousedown: { primary: 'माउसनीचे' },
    mouseenter: { primary: 'माउस_प्रवेश' },
    mouseleave: { primary: 'माउस_बाहर' },
    mousemove: { primary: 'माउस_मूव' },
    mouseout: { primary: 'माउस_आउट' },
    mouseover: { primary: 'माउस_ओवर' },
    mouseup: { primary: 'माउसऊपर' },
    reset: { primary: 'रीसेट' },
    resize: { primary: 'आकार_बदलें' },
    scroll: { primary: 'स्क्रॉल' },
    submit: { primary: 'जमा' },
    touchcancel: { primary: 'टच_रद्द' },
    touchend: { primary: 'टच_समाप्त' },
    touchmove: { primary: 'टच_मूव' },
    touchstart: { primary: 'टच_शुरू' },
    unload: { primary: 'अनलोड' },
  },
  logical: {
    and: { primary: 'और' },
    changes: { primary: 'बदलने पर' },
    contains: { primary: 'शामिल' },
    else: { primary: 'वरना' },
    empty: { primary: 'खाली' },
    end: { primary: 'समाप्त' },
    equals: { primary: 'बराबर' },
    exists: { primary: 'मौजूद' },
    has: { primary: 'है' },
    have: { primary: 'है' },
    includes: { primary: 'में_है' },
    is: { primary: 'है' },
    isNot: { primary: 'नहीं_है' },
    live: { primary: 'लाइव' },
    matches: { primary: 'मेल खाता' },
    not: { primary: 'नहीं' },
    or: { primary: 'या' },
    otherwise: { primary: 'अन्यथा' },
    then: { primary: 'फिर' },
    when: { primary: 'जब' },
    where: { primary: 'कहाँ' },
  },
  temporal: {
    every: { primary: 'हर' },
    forever: { primary: 'हमेशा' },
    h: { primary: 'घं' },
    hour: { primary: 'घंटा' },
    hours: { primary: 'घंटे' },
    millisecond: { primary: 'मिलीसेकंड' },
    milliseconds: { primary: 'मिलीसेकंड' },
    min: { primary: 'मि' },
    minute: { primary: 'मिनट' },
    minutes: { primary: 'मिनट' },
    ms: { primary: 'मिसे' },
    once: { primary: 'एक_बार' },
    s: { primary: 'से' },
    second: { primary: 'सेकंड' },
    seconds: { primary: 'सेकंड' },
    until: { primary: 'तक' },
    times: { primary: 'बार' },
  },
  values: {
    body: { primary: 'बॉडी' },
    closest: { primary: 'निकटतम' },
    detail: { primary: 'विवरण' },
    document: { primary: 'दस्तावेज़' },
    element: { primary: 'तत्व' },
    event: { primary: 'घटना' },
    false: { primary: 'झूठ' },
    first: { primary: 'पहला' },
    it: { primary: 'यह' },
    its: { primary: 'इसका' },
    last: { primary: 'अंतिम' },
    me: { primary: 'मैं' },
    my: { primary: 'मेरा' },
    myself: { primary: 'मैं_स्वयं' },
    next: { primary: 'अगला' },
    null: { primary: 'null' },
    parent: { primary: 'मूल' },
    previous: { primary: 'पिछला' },
    result: { primary: 'परिणाम' },
    target: { primary: 'लक्ष्य' },
    true: { primary: 'सच' },
    undefined: { primary: 'अपरिभाषित' },
    value: { primary: 'मान' },
    window: { primary: 'विंडो' },
    you: { primary: 'आप' },
    your: { primary: 'आपका' },
    yourself: { primary: 'स्वयं' },
  },
  attributes: {
    attribute: { primary: 'गुण' },
    attributes: { primary: 'गुण' },
    checked: { primary: 'चेक' },
    class: { primary: 'क्लास' },
    classes: { primary: 'क्लासेस' },
    disabled: { primary: 'अक्षम' },
    hidden: { primary: 'छिपा' },
    html: { primary: 'html' },
    properties: { primary: 'संपत्तियाँ' },
    property: { primary: 'संपत्ति' },
    readonly: { primary: 'केवल_पढ़ने' },
    required: { primary: 'आवश्यक' },
    selected: { primary: 'चयनित' },
    style: { primary: 'स्टाइल' },
    styles: { primary: 'स्टाइल्स' },
    text: { primary: 'पाठ' },
  },
  expressions: {
    at: { primary: 'पर' },
    characters: { primary: 'अक्षर' },
    children: { primary: 'बच्चे' },
    closest: { primary: 'निकटतम' },
    empty: { primary: 'खाली' },
    'ends with': { primary: 'पर_समाप्त' },
    first: { primary: 'पहला' },
    'ignoring case': { primary: 'केस_अनदेखा' },
    'joined by': { primary: 'द्वारा_जुड़ा' },
    last: { primary: 'अंतिम' },
    'mapped to': { primary: 'में_परिवर्तित' },
    next: { primary: 'अगला' },
    no: { primary: 'कोई नहीं' },
    parent: { primary: 'मूल' },
    prev: { primary: 'पिछ' },
    previous: { primary: 'पिछला' },
    random: { primary: 'यादृच्छिक' },
    some: { primary: 'कुछ' },
    'sorted by': { primary: 'द्वारा_क्रमबद्ध' },
    'split by': { primary: 'द्वारा_विभाजित' },
    'starts with': { primary: 'से_शुरू' },
    within: { primary: 'के_अंदर' },
  },
};

registerLexicon('hi', hindiLexicon);
